/**
 * Ticket autopilot — Claude agents that pick up and work tickets on their own.
 *
 * When enabled, a background loop advances the ticket queue one believable
 * step per tick:
 *
 *   1. unclaimed open/backlog ticket  → routed to the best-fit agent and
 *      claimed (skill match on title/spec/labels, load-balanced)
 *   2. claimed agent ticket           → moved to in-progress
 *   3. in-progress, no work logged    → agent posts a progress comment
 *   4. in-progress, work logged       → moved to review
 *   5. review                         → agent posts a resolution + done
 *
 * Doing exactly one action per tick keeps the board visibly animating and
 * the per-agent concurrency cap keeps the queue believable rather than
 * draining everything at once. All writes go through the same tickets
 * adapter the humans use, so every move lands on the append-only timeline
 * with the agent as the actor.
 */

import { useEffect, useState } from 'react';
import {
  addTicketComment,
  claimTicket,
  listTickets,
  setTicketStatus,
} from '../db/tickets';
import type { ApprovalItem, Ticket, TicketPriority } from '../db/types';
import * as argoWorker from './argoWorker';
import { listApprovals, queueApproval } from './approvals';
import type { AssignTicketAction } from './types';

/**
 * Governance mode for the autopilot.
 *
 *  - `propose` (default): the agent never silently routes a ticket to itself.
 *    The one consequential decision — *which* agent takes an unclaimed ticket —
 *    is queued as an `assign_ticket` proposal in the approvals inbox for an
 *    operator to approve. Once a human approves the assignment, the agent works
 *    the ticket through review on its own (it's now executing an approved
 *    mandate, exactly like a delegated human report). No silent auto-writes.
 *  - `auto`: full autonomy — the agent claims and works tickets with no
 *    human in the loop. Opt-in, clearly labelled.
 */
export type AutopilotMode = 'propose' | 'auto';

/** A Claude agent that can own and work tickets. */
export interface AgentProfile {
  /** Owner string stored on the ticket, e.g. "claude:fixer". */
  id: string;
  /** Short display name, e.g. "Fixer". */
  label: string;
  /** One-liner describing what this agent specialises in. */
  blurb: string;
  /** Lowercase keywords that route a ticket to this agent. */
  skills: string[];
  /** Max tickets this agent works at once (claimed + in-progress). */
  maxConcurrent: number;
}

export const AUTOPILOT_AGENTS: AgentProfile[] = [
  {
    id: 'claude:fixer',
    label: 'Fixer',
    blurb: 'Bugfixes, incidents, and regressions',
    skills: [
      'fix',
      'bug',
      'broken',
      'error',
      'hotfix',
      'incident',
      'regression',
      'crash',
      'fail',
      'patch',
      'repair',
    ],
    maxConcurrent: 2,
  },
  {
    id: 'claude:researcher',
    label: 'Researcher',
    blurb: 'Investigation, analysis, and spikes',
    skills: [
      'research',
      'investigate',
      'analyze',
      'analyse',
      'spike',
      'explore',
      'audit',
      'evaluate',
      'compare',
      'review',
      'report',
    ],
    maxConcurrent: 2,
  },
  {
    id: 'claude:worker',
    label: 'Worker',
    blurb: 'General build, wiring, and integrations',
    skills: [
      'wire',
      'build',
      'add',
      'create',
      'implement',
      'integrate',
      'setup',
      'set up',
      'migrate',
      'ship',
      'hook up',
      'connect',
    ],
    maxConcurrent: 2,
  },
];

const AGENT_BY_ID: Record<string, AgentProfile> = Object.fromEntries(
  AUTOPILOT_AGENTS.map((a) => [a.id, a]),
);

/** True when `owner` is one of our autopilot agents. */
export function isAgentOwner(owner: string | null | undefined): boolean {
  return !!owner && owner in AGENT_BY_ID;
}

/** Display label for an agent owner string, falling back to the raw value. */
export function agentLabel(owner: string | null | undefined): string {
  if (!owner) return '—';
  return AGENT_BY_ID[owner]?.label ?? owner;
}

const PRIORITY_RANK: Record<TicketPriority, number> = {
  p0: 0,
  p1: 1,
  p2: 2,
  p3: 3,
};

/** Marker prefixes so we can tell pickup / progress / resolution apart. */
const PICKUP_PREFIX = 'Picked up —';
const PROGRESS_PREFIX = 'Progress —';
const RESOLVE_PREFIX = 'Resolved —';

/**
 * Honesty marker appended to autopilot comments that are *simulated* — i.e.
 * locally-generated narration written while no Claude worker is connected.
 * It stops fabricated "work" from masquerading as real history in a ticket
 * the team is using for real; the moment `VITE_AGENT_API_URL` is set and the
 * worker does the work, the marker disappears on its own. See AUDIT.md §4B.
 * Appended as a suffix so the `*_PREFIX` `startsWith` checks still match.
 */
const SIM_SUFFIX = ' _(simulated)_';
function simSuffix(): string {
  return argoWorker.isConfigured() ? '' : SIM_SUFFIX;
}

function haystack(t: Ticket): string {
  return `${t.title} ${t.spec} ${t.labels.join(' ')}`.toLowerCase();
}

/** How many tickets an agent is actively working (claimed + in-progress). */
function loadFor(id: string, all: Ticket[]): number {
  return all.filter(
    (t) => t.owner === id && (t.status === 'claimed' || t.status === 'in-progress'),
  ).length;
}

/**
 * Pick the best-fit agent for a ticket: highest skill-keyword match, then
 * lightest current load as the tiebreak so work spreads across the roster.
 */
export function pickAgentForTicket(ticket: Ticket, all: Ticket[]): AgentProfile {
  const hay = haystack(ticket);
  const scored = AUTOPILOT_AGENTS.map((a) => ({
    agent: a,
    score: a.skills.reduce((n, kw) => (hay.includes(kw) ? n + 1 : n), 0),
  }));
  const top = Math.max(...scored.map((s) => s.score));
  const pool =
    top > 0
      ? scored.filter((s) => s.score === top).map((s) => s.agent)
      : AUTOPILOT_AGENTS.slice();
  pool.sort((a, b) => loadFor(a.id, all) - loadFor(b.id, all));
  return pool[0];
}

function hasProgress(t: Ticket): boolean {
  return t.comments.some(
    (c) => isAgentOwner(c.author) && c.body.startsWith(PROGRESS_PREFIX),
  );
}

// --- Believable agent narration --------------------------------------------

function pickupLine(t: Ticket): string {
  return `${PICKUP_PREFIX} taking ${t.id}, reading the spec and scoping the work now.`;
}

function progressLine(t: Ticket): string {
  const hay = haystack(t);
  let detail = 'making changes and validating against the spec';
  if (/test|qa|verify/.test(hay)) detail = 'writing tests and verifying behaviour';
  else if (/research|investigate|analyze|analyse|audit/.test(hay))
    detail = 'gathering findings and writing up the analysis';
  else if (/fix|bug|error|regression|crash/.test(hay))
    detail = 'reproduced the issue and landing the fix';
  else if (/wire|integrate|hook up|connect|build|add|create/.test(hay))
    detail = 'wiring it up and exercising the happy path';
  return `${PROGRESS_PREFIX} ${detail}.`;
}

function resolveLine(): string {
  return `${RESOLVE_PREFIX} work complete and self-reviewed; handing off for sign-off.`;
}

/**
 * Narration for the in-progress "work" step. This is the meaningful step
 * where an agent actually does the work, so when the hosted Argo worker is
 * configured we route it through the real AWS → Claude pipeline and use
 * what it reports back. If the worker is absent or the call fails we keep
 * the believable simulation, so the board never stalls. The `PROGRESS_PREFIX`
 * is always preserved so `hasProgress()` still advances the ticket to review.
 */
async function workTicketNarration(t: Ticket, all: Ticket[]): Promise<string> {
  if (argoWorker.isConfigured()) {
    try {
      const agent = AGENT_BY_ID[t.owner as string];
      const out = await argoWorker.runAgent(
        {
          id: t.owner as string,
          name: agent?.label ?? (t.owner as string),
          template: 'ticket-worker',
          persona: t.owner as string,
        },
        { ticket: t, tickets: all },
      );
      const text = out.text.trim();
      return text.startsWith(PROGRESS_PREFIX) ? text : `${PROGRESS_PREFIX} ${text}`;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Autopilot] worker run failed, using simulated narration:', err);
    }
  }
  // Reached only when the worker is unconfigured or its call failed — this is
  // fabricated narration, so mark it as such.
  return progressLine(t) + SIM_SUFFIX;
}

/** One step of agent work. Returns a short event, or null if idle. */
export interface AutopilotEvent {
  /**
   * `propose` is emitted in governed mode when the autopilot queues an
   * assignment for operator approval instead of routing the ticket itself.
   */
  kind: 'claim' | 'start' | 'progress' | 'review' | 'done' | 'propose';
  ticketId: string;
  agent: string;
  text: string;
}

/**
 * True when an assignment for this ticket has already been put to the
 * operator (still pending, or previously resolved). Used to avoid
 * re-proposing the same routing decision on every tick — a pending
 * proposal awaits a human, and a dismissed one means "don't route this".
 *
 * Exported (pure) so the governance contract can be unit-tested without
 * driving the store.
 */
export function hasAssignProposal(
  ticketId: string,
  approvals: Pick<ApprovalItem, 'action'>[],
): boolean {
  return approvals.some(
    (a) =>
      a.action?.kind === 'assign_ticket' && a.action?.ticketId === ticketId,
  );
}

/**
 * The assignment an agent *proposes* when, in governed mode, it would route an
 * unclaimed ticket to itself. This is a proposal — it goes to the approvals
 * inbox and only writes once a human approves it. Pure + exported so the
 * routing contract is unit-testable.
 */
export function routeProposalFor(ticket: Ticket, agent: AgentProfile): AssignTicketAction {
  return {
    kind: 'assign_ticket',
    ticketId: ticket.id,
    ticketTitle: ticket.title,
    owner: agent.id,
    fromOwner: null,
  };
}

export async function autopilotTick(
  mode: AutopilotMode = 'auto',
): Promise<AutopilotEvent | null> {
  const tickets = await listTickets();

  // 1. Finish anything in review.
  const review = tickets.find((t) => isAgentOwner(t.owner) && t.status === 'review');
  if (review) {
    await addTicketComment(review.id, review.owner!, resolveLine() + simSuffix());
    await setTicketStatus(review.id, 'done', review.owner!);
    return {
      kind: 'done',
      ticketId: review.id,
      agent: review.owner!,
      text: `${agentLabel(review.owner)} finished ${review.id}`,
    };
  }

  // 2. In-progress with work logged → move to review.
  const ready = tickets.find(
    (t) => isAgentOwner(t.owner) && t.status === 'in-progress' && hasProgress(t),
  );
  if (ready) {
    await setTicketStatus(ready.id, 'review', ready.owner!);
    return {
      kind: 'review',
      ticketId: ready.id,
      agent: ready.owner!,
      text: `${agentLabel(ready.owner)} sent ${ready.id} to review`,
    };
  }

  // 3. In-progress without work logged → post a progress comment.
  const working = tickets.find(
    (t) => isAgentOwner(t.owner) && t.status === 'in-progress' && !hasProgress(t),
  );
  if (working) {
    const narration = await workTicketNarration(working, tickets);
    await addTicketComment(working.id, working.owner!, narration);
    return {
      kind: 'progress',
      ticketId: working.id,
      agent: working.owner!,
      text: `${agentLabel(working.owner)} logged progress on ${working.id}`,
    };
  }

  // 4. Claimed agent ticket → start it.
  const claimed = tickets.find(
    (t) => isAgentOwner(t.owner) && t.status === 'claimed',
  );
  if (claimed) {
    await setTicketStatus(claimed.id, 'in-progress', claimed.owner!);
    return {
      kind: 'start',
      ticketId: claimed.id,
      agent: claimed.owner!,
      text: `${agentLabel(claimed.owner)} started ${claimed.id}`,
    };
  }

  // 5. Operator approved an assignment → the ticket now carries an agent owner
  //    but is still open/backlog. Pick it up so the agent starts the work it
  //    was approved to do. (In `auto` mode this never fires because step 6
  //    claims directly; it's the hand-off point from the approvals inbox.)
  const approvedAssign = tickets.find(
    (t) => isAgentOwner(t.owner) && (t.status === 'open' || t.status === 'backlog'),
  );
  if (approvedAssign) {
    await claimTicket(approvedAssign.id, approvedAssign.owner!);
    await addTicketComment(
      approvedAssign.id,
      approvedAssign.owner!,
      pickupLine(approvedAssign) + simSuffix(),
    );
    return {
      kind: 'claim',
      ticketId: approvedAssign.id,
      agent: approvedAssign.owner!,
      text: `${agentLabel(approvedAssign.owner)} picked up ${approvedAssign.id}`,
    };
  }

  // 6. Unclaimed open/backlog → route to the best-fit agent.
  const queue = tickets
    .filter((t) => (t.status === 'open' || t.status === 'backlog') && !t.owner)
    .sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);

  // In governed mode, routing is a *proposal*, not a write: surface the
  // already-queued ones so we don't double-propose, then queue the next.
  const priorApprovals = mode === 'propose' ? await listApprovals() : [];

  for (const t of queue) {
    const agent = pickAgentForTicket(t, tickets);
    if (loadFor(agent.id, tickets) >= agent.maxConcurrent) continue;

    if (mode === 'propose') {
      // Skip anything already put to the operator (pending or resolved).
      if (hasAssignProposal(t.id, priorApprovals)) continue;
      await queueApproval(
        routeProposalFor(t, agent),
        'autopilot',
        `Routing ${t.priority.toUpperCase()} ticket to ${agent.label} (best skill match). Approve to let the agent work it.`,
      );
      return {
        kind: 'propose',
        ticketId: t.id,
        agent: agent.id,
        text: `Proposed routing ${t.id} → ${agent.label} (awaiting approval)`,
      };
    }

    // Autonomous mode: claim directly.
    await claimTicket(t.id, agent.id);
    await addTicketComment(t.id, agent.id, pickupLine(t) + simSuffix());
    return {
      kind: 'claim',
      ticketId: t.id,
      agent: agent.id,
      text: `${agent.label} picked up ${t.id}`,
    };
  }

  return null;
}

const STORAGE_KEY = 'argo-os:ticket-autopilot';
const MODE_KEY = 'argo-os:ticket-autopilot-mode';

/** Read the persisted enabled flag (off by default). */
export function readAutopilotEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

function writeAutopilotEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    /* ignore */
  }
}

/** Read the persisted governance mode. Governed (`propose`) by default. */
export function readAutopilotMode(): AutopilotMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'auto' ? 'auto' : 'propose';
  } catch {
    return 'propose';
  }
}

function writeAutopilotMode(mode: AutopilotMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export interface UseTicketAutopilot {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  toggle: () => void;
  /**
   * Governance mode. `propose` (default) queues assignments for approval;
   * `auto` lets agents route + work tickets unattended.
   */
  mode: AutopilotMode;
  setMode: (mode: AutopilotMode) => void;
  /** Most recent action the autopilot took, for a status line. */
  lastEvent: AutopilotEvent | null;
  /** Rolling window of recent actions, newest-first, for a live ticker. */
  recentEvents: AutopilotEvent[];
  /** True while a tick is mid-flight. */
  busy: boolean;
  /**
   * Whether agent work is routed through the real hosted Claude worker
   * (true) or the in-app simulation (false). Surfaced as a status pill so
   * it's obvious which mode is live.
   */
  workerConfigured: boolean;
}

/**
 * Drive the autopilot loop. While enabled, runs `autopilotTick` every
 * `intervalMs`; the loop self-schedules so ticks never overlap. The enabled
 * flag is persisted so it survives reloads.
 */
export function useTicketAutopilot(intervalMs = 4000): UseTicketAutopilot {
  const [enabled, setEnabledState] = useState<boolean>(() => readAutopilotEnabled());
  const [mode, setModeState] = useState<AutopilotMode>(() => readAutopilotMode());
  const [lastEvent, setLastEvent] = useState<AutopilotEvent | null>(null);
  const [recentEvents, setRecentEvents] = useState<AutopilotEvent[]>([]);
  const [busy, setBusy] = useState(false);

  const setEnabled = (on: boolean) => {
    writeAutopilotEnabled(on);
    setEnabledState(on);
  };
  const toggle = () => setEnabled(!enabled);

  const setMode = (next: AutopilotMode) => {
    writeAutopilotMode(next);
    setModeState(next);
  };

  useEffect(() => {
    if (!enabled) return;
    // Per-effect-run cancel flag (NOT a shared ref): under React StrictMode
    // the effect mounts → cleans up → mounts again, and a shared ref would be
    // reset to false by the second mount, un-cancelling the first loop's tail
    // and leaving two timer chains racing. A closure-local flag keeps each
    // run's cleanup authoritative, so exactly one loop is ever live.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const loop = async () => {
      if (cancelled) return;
      setBusy(true);
      try {
        const ev = await autopilotTick(mode);
        if (!cancelled && ev) {
          setLastEvent(ev);
          setRecentEvents((prev) => [ev, ...prev].slice(0, 6));
        }
      } catch {
        /* swallow — a bad tick shouldn't kill the loop */
      } finally {
        if (!cancelled) setBusy(false);
      }
      if (!cancelled) timer = setTimeout(loop, intervalMs);
    };

    loop();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, intervalMs, mode]);

  return {
    enabled,
    setEnabled,
    toggle,
    mode,
    setMode,
    lastEvent,
    recentEvents,
    busy,
    workerConfigured: argoWorker.isConfigured(),
  };
}
